import { ReactNode, useCallback, useEffect, useRef, useState } from "react"

import LotteryConfetti, {
	ConfettiPiece,
	newConfettiPieces,
} from "./LotteryConfetti"
import LotteryFireworks, { Firework, newFireworks } from "./LotteryFireworks"
import styles from "./LotteryRoulette.module.scss"
import { LOTTERY_WINNER_COUNT } from "./lotteryPrize"
import { LotterySound, SpinEasing, createLotterySound } from "./lotterySound"
import { isLotterySoundMuted, setLotterySoundMuted } from "./lotteryStorage"

// 再予約 10 万円ルーレットの盤面 (12 時方向から時計回り).
// backend の domain/model/lottery.py RERESERVATION_100K.wheel_multipliers と揃えること.
// 盤面は抽選ごとに違う (請求側 CANCEL_FEE_100K は 1〜5 の 5 分割) ので、
// 請求側は API が返す wheel_multipliers を props で渡す.
// 実際に当たるのは必ず 3 倍以上だが、盤面には 1・2 倍も置いて「当たった感」を作る.
const RERESERVATION_WHEEL_MULTIPLIERS: readonly number[] = [1, 2, 3, 4]

// 再予約側の盤面に載っている一番大きい倍率. 再予約バナーの「最大 N 倍」の訴求に使う.
export const LOTTERY_WHEEL_MAX_MULTIPLIER = Math.max(
	...RERESERVATION_WHEEL_MULTIPLIERS
)

// 止まるまでに回す周回数と、回転アニメーションの長さ・緩急.
// 長さと緩急はここが正. scss へは CSS 変数 (--spin-duration / --spin-easing) で渡し、
// カチカチ音の間隔の計算にも同じ値を使う (盤面と音がずれないように).
const SPIN_TURNS = 6
const SPIN_DURATION_MS = 4200
const SPIN_EASING: SpinEasing = { x1: 0.12, y1: 0.66, x2: 0.16, y2: 1 }

// scss の .wheel / .wheelLabel の transition が読む CSS 変数.
type SpinStyle = React.CSSProperties & {
	"--spin-duration": string
	"--spin-easing": string
}

const SPIN_STYLE: SpinStyle = {
	"--spin-duration": `${SPIN_DURATION_MS}ms`,
	"--spin-easing": `cubic-bezier(${SPIN_EASING.x1}, ${SPIN_EASING.y1}, ${SPIN_EASING.x2}, ${SPIN_EASING.y2})`,
}

// 確定した倍率のセグメントを、盤面上部の針の位置まで持ってくる回転角 (deg).
// セグメント i は [i*segmentAngle, (i+1)*segmentAngle) を占めるので、その中心を 0 度へ戻す.
const rotationToLand = (
	wheelMultipliers: readonly number[],
	multiplier: number
): number => {
	const segmentAngle = 360 / wheelMultipliers.length
	const index = wheelMultipliers.indexOf(multiplier)
	// 盤面に無い倍率が来たら演出だけ回して最後 (一番大きい倍率) に寄せる. 表示する倍率は props の値を使う.
	const targetIndex = index >= 0 ? index : wheelMultipliers.length - 1
	return SPIN_TURNS * 360 - (targetIndex * segmentAngle + segmentAngle / 2)
}

// 偶数番目は淡い色、奇数番目は濃い色のセグメントにする.
// 分割数が奇数だと最後と最初が両方淡い色で隣り合って境目が消えるので、最後だけ中間色にする.
// 色そのものは scss (.wheel) の CSS 変数で持つ.
const isPaleSegment = (index: number): boolean => index % 2 === 0

const segmentColor = (index: number, count: number): string => {
	if (count % 2 === 1 && index === count - 1) {
		return "var(--wheel-segment-mid)"
	}
	if (isPaleSegment(index)) {
		return "var(--wheel-segment-pale)"
	}
	return "var(--wheel-segment-strong)"
}

const wheelBackground = (count: number): string => {
	const segmentAngle = 360 / count
	const stops = Array.from({ length: count }, (_, index) => {
		const from = index * segmentAngle
		const to = (index + 1) * segmentAngle
		return `${segmentColor(index, count)} ${from}deg ${to}deg`
	})
	return `conic-gradient(from 0deg, ${stops.join(", ")})`
}

// 止まったセグメント (針の真下の 1 セグメント分) だけを白く光らせる面.
const winWedgeBackground = (count: number): string => {
	const segmentAngle = 360 / count
	return `conic-gradient(from ${-segmentAngle / 2}deg, rgba(255, 255, 255, 0.75) 0deg ${segmentAngle}deg, rgba(255, 255, 255, 0) ${segmentAngle}deg 360deg)`
}

// 淡い色のセグメントは白文字だと読めないので、ラベルの色を切り替える.
const wheelLabelClassName = (index: number): string => {
	if (isPaleSegment(index)) {
		return `${styles.wheelLabel} ${styles.wheelLabelOnPale}`
	}
	return styles.wheelLabel
}

const prefersReducedMotion = (): boolean =>
	typeof window !== "undefined" &&
	typeof window.matchMedia === "function" &&
	window.matchMedia("(prefers-reduced-motion: reduce)").matches

// 結果の数字の周りで瞬く星の位置 (結果の枠の左上からの割合, %) と、瞬き始める時刻のずれ (s).
const RESULT_STARS = [
	{ id: 0, left: 8, top: 22, delay: 0 },
	{ id: 1, left: 90, top: 18, delay: 0.5 },
	{ id: 2, left: 17, top: 60, delay: 0.9 },
	{ id: 3, left: 84, top: 56, delay: 0.3 },
	{ id: 4, left: 30, top: 34, delay: 1.2 },
	{ id: 5, left: 72, top: 38, delay: 0.7 },
]

// 結果が出てから、結果とボタンが見える位置まで自動でスクロールし始めるまでの時間 (ms).
// 止まった盤面と紙吹雪を少し見せてから動かす.
const RESULT_SCROLL_DELAY_MS = 700

// autoSpin で開いた時に、表示されてから回り始めるまでの時間 (ms).
// 開いた瞬間に回すと盤面が目に入る前に動き出して「何が起きたか」分からないので、
// 見出し (当選確率 最大 N 倍) と盤面を読む時間として数秒置く.
// 初回描画 (rotation 0) を済ませてから回転角を変えないと CSS transition が発火しない、という事情もある.
const AUTO_SPIN_DELAY_MS = 2000

// 止まった瞬間の振動 (対応端末のみ. iPhone は非対応).
const vibrateOnWin = () => {
	if (typeof navigator === "undefined") {
		return
	}
	if (typeof navigator.vibrate !== "function") {
		return
	}
	navigator.vibrate([40, 50, 120])
}

type Phase = "ready" | "spinning" | "result"

// 結果表示の注記 (再予約側). 応募条件に合わせて文言を差し替えられるようにしている.
const rereservationResultNote = (multiplier: number): ReactNode => (
	<>
		この画面から再予約して<strong>ご来店</strong>いただくと、
		{multiplier} 倍の当選確率で抽選に参加できます。
	</>
)

type Props = {
	// くじ券の発行時にサーバが抽選して DB に保存した倍率. 何度開いても同じ値.
	multiplier: number
	// 盤面に並べる倍率 (12 時方向から時計回り). 省略時は再予約側の盤面.
	wheelMultipliers?: readonly number[]
	// 見出し下の「抽選で ○○ に」の ○○ 部分. 省略時は再予約側の当選人数.
	winnerText?: string
	// 結果表示の注記. 省略時は再予約側 (再予約して来店で応募) の文言.
	resultNote?: ReactNode
	// 結果表示のあとに押すボタンの文言. 省略時は再予約側の「空き枠を見る」.
	resultButtonLabel?: string
	// 賞品の表示文言 (例: "100,000円分のポイント"). lotteryPrizeText() で作ること.
	prizeText: string
	customerName: string
	// 表示された直後に「回す」を押さなくても自動で回し始めるか (請求側の見せ方 A/B の auto_open 群).
	// お客様のタップを経ていないので効果音は鳴らさない (ブラウザが user の操作なしの音を止めるため.
	// 鳴らそうとすると AudioContext が suspended のまま残り、後のタップで遅れて鳴り出す).
	autoSpin?: boolean
	// 回り始めた時に呼ばれる (「回す」を押した時、または autoSpin で自動で回り始めた時).
	// 親はここから結果表示まで、モーダルの外側を暗くする.
	onSpinStart: () => void
	// 演出が終わって倍率が確定した時 (= 回し終わった時) に呼ばれる.
	onSpun: () => void
	onClose: () => void
}

// 倍率アップルーレット本体. 「回す」を押すと必ず multiplier のセグメントで止まる.
// 倍率の抽選そのものはサーバ側で済んでいて (DB に保存済み)、ここは結果を見せるための演出に徹する.
// このルーレットで決まるのは当選確率の倍率であって、賞品が当たるかどうかではない.
// 賞品の抽選と取り違えられないよう、見出しは「倍率アップ」を主語にしている.
const LotteryRoulette = ({
	multiplier,
	wheelMultipliers = RERESERVATION_WHEEL_MULTIPLIERS,
	winnerText = `${LOTTERY_WINNER_COUNT} 名様`,
	resultNote,
	resultButtonLabel = "空き枠を見る",
	prizeText,
	customerName,
	autoSpin = false,
	onSpinStart,
	onSpun,
	onClose,
}: Props) => {
	const [phase, setPhase] = useState<Phase>("ready")
	const [rotation, setRotation] = useState(0)
	const [confetti, setConfetti] = useState<ConfettiPiece[]>([])
	const [confettiOriginY, setConfettiOriginY] = useState(0)
	const [fireworks, setFireworks] = useState<Firework[]>([])
	const [isMuted, setIsMuted] = useState(() => isLotterySoundMuted())
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const soundRef = useRef<LotterySound | null>(null)
	const wheelAreaRef = useRef<HTMLDivElement>(null)
	const closeButtonRef = useRef<HTMLButtonElement>(null)

	const segmentAngle = 360 / wheelMultipliers.length
	const maxMultiplier = Math.max(...wheelMultipliers)

	useEffect(() => {
		return () => {
			if (timerRef.current !== null) {
				clearTimeout(timerRef.current)
			}
			if (soundRef.current !== null) {
				soundRef.current.dispose()
			}
		}
	}, [])

	// 小さい端末では結果表示がモーダルに収まらず、下が隠れる (中はスクロールできる).
	// 結果と「空き枠を見る」を見落とさないよう、一番下のボタンが見える位置まで自動で送る.
	// 収まっている端末では何も起きない (block: "nearest").
	useEffect(() => {
		if (phase !== "result") {
			return
		}
		const timer = setTimeout(() => {
			const closeButton = closeButtonRef.current
			if (closeButton === null) {
				return
			}
			let behavior: ScrollBehavior = "smooth"
			if (prefersReducedMotion()) {
				behavior = "auto"
			}
			closeButton.scrollIntoView({ block: "nearest", behavior })
		}, RESULT_SCROLL_DELAY_MS)
		return () => clearTimeout(timer)
	}, [phase])

	const handleToggleMute = useCallback(() => {
		const next = !isMuted
		setIsMuted(next)
		setLotterySoundMuted(next)
	}, [isMuted])

	// 倍率が確定した瞬間の演出 (紙吹雪・花火・ファンファーレ・振動).
	const celebrate = useCallback(
		(sound: LotterySound | null, withMotion: boolean) => {
			const wheelArea = wheelAreaRef.current
			let launchedFireworks: Firework[] = []
			if (withMotion && wheelArea !== null) {
				// 紙吹雪と花火は盤面を基準に出す. 見出しの行数で盤面の位置が変わるので、その場で測る.
				const originY = wheelArea.offsetTop + wheelArea.offsetHeight / 2
				launchedFireworks = newFireworks(originY)
				setConfettiOriginY(originY)
				setConfetti(newConfettiPieces())
				setFireworks(launchedFireworks)
			}
			if (sound !== null) {
				sound.playWin(multiplier, multiplier === maxMultiplier)
				// 花火の音は、画面の花火が開く時刻と同じ値から鳴らす (花火を出さない時は鳴らさない).
				sound.playFireworks(
					launchedFireworks.map((firework) => firework.delayMs)
				)
				// 振動も音と同じく周りに伝わるので、音を消している時は出さない.
				vibrateOnWin()
			}
		},
		[maxMultiplier, multiplier]
	)

	// 回し終わった時の処理. 演出を出し、結果表示に切り替えて、親に「回した」ことを伝える.
	const finishSpin = useCallback(
		(sound: LotterySound | null, withMotion: boolean) => {
			celebrate(sound, withMotion)
			setPhase("result")
			onSpun()
		},
		[celebrate, onSpun]
	)

	// 回し始める. sound は呼び出し側が用意する (音を出せるのは user の操作の中で作った時だけ).
	const startSpin = useCallback(
		(sound: LotterySound | null) => {
			if (phase !== "ready") {
				return
			}
			soundRef.current = sound
			onSpinStart()
			const landing = rotationToLand(wheelMultipliers, multiplier)
			if (prefersReducedMotion()) {
				// motion 軽減設定の user には回さず結果だけ見せる (回転量は 1 周未満に丸める).
				setRotation(landing % 360)
				finishSpin(sound, false)
				return
			}
			setPhase("spinning")
			setRotation(landing)
			if (sound !== null) {
				sound.playSpin(SPIN_DURATION_MS, landing, SPIN_EASING)
			}
			timerRef.current = setTimeout(() => {
				finishSpin(sound, true)
			}, SPIN_DURATION_MS)
		},
		[finishSpin, multiplier, onSpinStart, phase, wheelMultipliers]
	)

	const handleSpin = useCallback(() => {
		if (phase !== "ready") {
			return
		}
		// 音は user の操作 (このタップ) の中で用意しないとブラウザに止められる.
		let sound: LotterySound | null = null
		if (!isMuted) {
			sound = createLotterySound()
		}
		startSpin(sound)
	}, [isMuted, phase, startSpin])

	// autoSpin: 表示された直後に、タップなしで回し始める (音なし).
	useEffect(() => {
		if (!autoSpin) {
			return
		}
		const timer = setTimeout(() => {
			startSpin(null)
		}, AUTO_SPIN_DELAY_MS)
		return () => clearTimeout(timer)
		// マウント直後の 1 度だけ. startSpin は phase が変わると作り直されるが、回し始めた後に再び回さない.
	}, [])

	let rouletteClassName = styles.roulette
	let wheelAreaClassName = styles.wheelArea
	if (phase === "spinning") {
		wheelAreaClassName = `${styles.wheelArea} ${styles.wheelAreaSpinning}`
	}
	if (phase === "result") {
		rouletteClassName = `${styles.roulette} ${styles.rouletteWin}`
		wheelAreaClassName = `${styles.wheelArea} ${styles.wheelAreaWin}`
	}

	let muteIcon: string
	if (isMuted) {
		muteIcon = "🔇"
	} else {
		muteIcon = "🔊"
	}

	return (
		<div className={rouletteClassName} style={SPIN_STYLE}>
			<LotteryConfetti pieces={confetti} originY={confettiOriginY} />
			<LotteryFireworks fireworks={fireworks} />
			<div className={styles.eyebrow}>
				<span className={styles.eyebrowBadge}>期間限定</span>
				<span>倍率アップチャンス</span>
				<button
					type="button"
					className={styles.muteButton}
					onClick={handleToggleMute}
					aria-label="効果音を消す"
					aria-pressed={isMuted}
					disabled={phase === "spinning"}
				>
					<span aria-hidden="true">{muteIcon}</span>
				</button>
			</div>
			<div className={styles.customer}>{customerName}様 限定</div>
			{/* 名前 (○○ルーレット) ではなく、回すと何が得られるかを見出しにする */}
			<h2 className={styles.title}>
				<span className={styles.titleLead}>ルーレットを回して</span>
				<span className={styles.titleMain}>
					当選確率
					<span className={styles.titleMax}>
						最大
						<span className={styles.titleNumber}>{maxMultiplier}</span>倍
					</span>
				</span>
			</h2>
			<p className={styles.lead}>
				抽選で
				<span className={styles.leadStrong}>{winnerText}</span>に
				<span className={styles.leadStrong}>{prizeText}</span>
				が当たる
			</p>

			<div className={wheelAreaClassName} ref={wheelAreaRef}>
				<div className={styles.wheelGlow} aria-hidden="true" />
				<div className={styles.wheelBurst} aria-hidden="true" />
				<div className={styles.pointer} aria-hidden="true" />
				{/* 初期 rotation は 0 なので、回すまで transition は発火しない. */}
				<div
					className={styles.wheel}
					style={{
						transform: `rotate(${rotation}deg)`,
						background: wheelBackground(wheelMultipliers.length),
					}}
				>
					{wheelMultipliers.map((value, index) => {
						const labelAngle = index * segmentAngle + segmentAngle / 2
						return (
							<span
								key={value}
								className={wheelLabelClassName(index)}
								style={{
									// 1. rotate(labelAngle) + translateY でセグメントの中心へ配置
									// 2. rotate(-labelAngle) で盤面内での文字の傾きを戻す
									// 3. rotate(-rotation) で盤面自体の回転を打ち消し、回転中も文字を正立させる
									//    (wheel と同じ transition を当てているので、回転と同期して動く)
									transform: `rotate(${labelAngle}deg) translateY(-62px) rotate(${-labelAngle}deg) rotate(${-rotation}deg)`,
								}}
							>
								{value}
								<small>倍</small>
							</span>
						)
					})}
				</div>
				{/* 止まったセグメント (針の真下) を点滅させる. 盤面の上に重ねるだけで、盤面とは一緒に回さない. */}
				<div
					className={styles.wheelWinWedge}
					style={{ background: winWedgeBackground(wheelMultipliers.length) }}
					aria-hidden="true"
				/>
				<div className={styles.wheelHub} aria-hidden="true">
					<span>倍率</span>
				</div>
			</div>

			{phase === "result" ? (
				<div className={styles.result} role="status">
					<div className={styles.resultRays} aria-hidden="true" />
					{RESULT_STARS.map((star) => (
						<span
							key={star.id}
							className={styles.resultStar}
							aria-hidden="true"
							style={{
								left: `${star.left}%`,
								top: `${star.top}%`,
								animationDelay: `${star.delay}s`,
							}}
						/>
					))}
					<div className={styles.resultCongrats}>
						<span aria-hidden="true">🎉</span>
						おめでとうございます！
						<span aria-hidden="true">🎉</span>
					</div>
					<div className={styles.resultLabel}>あなたの当選確率が</div>
					<div className={styles.resultValue}>
						<span className={styles.resultNumber}>{multiplier}</span>
						<span className={styles.resultUnit}>倍</span>
						<span className={styles.resultGet}>にアップ！</span>
					</div>
					<p className={styles.resultNote}>
						{resultNote ?? rereservationResultNote(multiplier)}
					</p>
				</div>
			) : (
				<p className={styles.hint} aria-live="polite">
					{phase === "spinning"
						? "ルーレット回転中…"
						: "ルーレットは 1 回だけ。結果は変わりません。"}
				</p>
			)}

			{phase === "result" ? (
				<button
					type="button"
					className={styles.primaryButton}
					onClick={onClose}
				>
					{resultButtonLabel}
				</button>
			) : (
				<button
					type="button"
					className={styles.primaryButton}
					onClick={handleSpin}
					disabled={phase === "spinning"}
				>
					{phase === "spinning" ? "回転中…" : "ルーレットを回す"}
				</button>
			)}

			<button
				type="button"
				className={styles.textButton}
				onClick={onClose}
				ref={closeButtonRef}
			>
				閉じる
			</button>
		</div>
	)
}

export default LotteryRoulette
